/**
 * useWorkspaceAgentTools — FutureBot registration for the Person Workspace.
 *
 * No page tools are needed here: the global Quantum Matrix tools cover the
 * lender/guideline work. What this page contributes is CONTEXT — the selected
 * deal's live loan scenario (buildDealScenario) lands in
 * pageContext.referenceData.dealScenario so the model can call qm_match /
 * qm_ask with real deal facts without re-asking the user.
 *
 * State is read through a ref so the memoized registration stays stable
 * (useAgentPage re-registers only on real changes) while getContext always
 * sees the currently selected deal.
 */

import { useMemo, useRef } from 'react';
import {
  buildDealScenario,
  describeDealScenario,
} from '../../../components/FutureBot/dealScenario';

const WORKSPACE_SUGGESTIONS = [
  'What lender options fit this deal?',
  'Which lenders allow gift funds on this program?',
  'What does the guideline say about DSCR reserves?',
];

export function useWorkspaceAgentTools({
  contact,
  selectedDeal,
  // The classic Deal Page reuses this registration with its own identity.
  pageId = 'crm-person-workspace',
  title = 'Person Workspace',
}) {
  const dataRef = useRef({});
  dataRef.current = { contact, selectedDeal };

  return useMemo(
    () => ({
      pageId,
      title,
      getContext: async () => {
        const { contact: person, selectedDeal: deal } = dataRef.current;
        const personName = person
          ? `${person.firstName || ''} ${person.lastName || ''}`.trim()
          : '';

        // On party deals (this person is the agent/title/co-borrower) the
        // borrower is someone else — let the deal's own data name them.
        const isBorrowerDeal = !deal?.relationshipRole || deal.relationshipRole === 'primary-contact';
        const scenario = buildDealScenario(deal, {
          borrowerName: isBorrowerDeal && personName ? personName : undefined,
        });

        const summaryBits = [];
        if (personName) summaryBits.push(`Viewing ${personName}`);
        if (deal) {
          summaryBits.push(
            `selected deal: ${deal.dealName || deal.id}${deal.stage ? ` (stage ${deal.stage})` : ''}`
          );
          const line = describeDealScenario(scenario);
          if (line) summaryBits.push(line);
        } else {
          summaryBits.push('no deal selected');
        }

        return {
          ...(scenario ? { referenceData: { dealScenario: scenario } } : {}),
          stateSummary: summaryBits.join(' — '),
        };
      },
      suggestions: WORKSPACE_SUGGESTIONS,
    }),
    [pageId, title]
  );
}

export default useWorkspaceAgentTools;
